import * as React from 'react';
import styles from './index.less';
import FCoverImage from '@/components/FCoverImage';
import FComponentsLib from '@freelog/components-lib';
import { FI18n, FServiceAPI } from '@freelog/tools-lib';
import type { ThemeDependencyPresentableItem } from '@/models/exhibitInfoPage';
import FAttrsAndConfigs, { type IAttrsAndConfigsItem } from '@/components/FAttrsAndConfigs';
import fMessage from '@/components/fMessage';

interface FThemeDependencyPluginsProps {
  items: ThemeDependencyPresentableItem[];
  /** 当前主题展品 presentableId */
  exhibitPresentableId: string;
  /** 保存成功后刷新展品详情 */
  onAfterDependencyAttrsSave?: () => void;
}

function optionRowsToReadonlyAttrs(
  rows: { label: string; value: string }[],
  presentableId: string,
): IAttrsAndConfigsItem[] {
  return rows.map((row, idx) => ({
    key: `${presentableId}_dep_opt_${idx}`,
    name: row.label,
    description: '',
    value: row.value,
    configs: {
      item_Deletable: false,
      key_Editable: false,
      name_Editable: false,
      description_Editable: false,
      value_Editable: false,
    },
  }));
}

function attrsToDependencyRewritePayload(
  items: IAttrsAndConfigsItem[],
): { key: string; value: string; remark: string; name: string }[] {
  return items
    .filter((i) => i.configs.value_Editable)
    .map((i) => ({
      key: i.key,
      value: i.value ?? '',
      remark: i.description ?? '',
      name: i.name ?? '',
    }));
}

function FThemeDependencyPlugins({
  items,
  exhibitPresentableId,
  onAfterDependencyAttrsSave,
}: FThemeDependencyPluginsProps) {
  const [$expanded, set$expanded] = React.useState<Record<string, boolean>>({});

  if (!items.length) {
    return null;
  }

  const visibleItems = items.filter((item) => {
    const attrsSource =
      item.optionalAttrsItems && item.optionalAttrsItems.length > 0
        ? item.optionalAttrsItems
        : optionRowsToReadonlyAttrs(item.optionRows, item.presentableId);
    return attrsSource.length > 0;
  });

  if (!visibleItems.length) {
    return null;
  }

  return (
    <div className={styles.wrap}>
      {visibleItems.map((item) => {
        const open = !!$expanded[item.presentableId];
        const attrsSource: IAttrsAndConfigsItem[] =
          item.optionalAttrsItems && item.optionalAttrsItems.length > 0
            ? item.optionalAttrsItems
            : optionRowsToReadonlyAttrs(item.optionRows, item.presentableId);
        const hasFields = attrsSource.length > 0;

        const metaNodes: React.ReactNode[] = [];
        if (item.useAvatar) {
          metaNodes.push(
            <img key='a' className={styles.avatar} src={item.useAvatar} alt={''} />,
          );
        }
        if (item.username) {
          metaNodes.push(<span key='u'>{item.username}</span>);
        }
        if (item.resourceTypes.length) {
          metaNodes.push(<span key='t'>{item.resourceTypes.join('/')}</span>);
        }
        if (item.updateDate) {
          metaNodes.push(<span key='d'>{`更新时间${item.updateDate}`}</span>);
        }
        if (item.version) {
          metaNodes.push(
            <span key='v'>
              {
                // 展示版本（与展品页其它位置一致）
                `${FI18n.i18nNext.t('edit_exhibit_version')}${item.version}`
              }
            </span>,
          );
        }

        const isMockItem = item.presentableId.startsWith('__mock');

        return (
          <div key={item.presentableId} className={styles.card}>
            <div className={styles.headerRow}>
              <div className={styles.cover}>
                <FCoverImage src={item.cover} width={56} />
              </div>
              <div className={styles.main}>
                <div className={styles.title}>{item.title}</div>
                <div className={styles.metaRow}>
                  <div className={styles.metaLeft}>
                    {metaNodes.map((node, idx) => (
                      <React.Fragment key={idx}>
                        {idx > 0 && <span className={styles.divider} />}
                        {node}
                      </React.Fragment>
                    ))}
                  </div>
                  {hasFields && (
                    <FComponentsLib.FTextBtn
                      className={styles.toggleBtn}
                      style={{ fontSize: 12 }}
                      onClick={() => {
                        set$expanded((prev) => ({
                          ...prev,
                          [item.presentableId]: !prev[item.presentableId],
                        }));
                      }}
                    >
                      {open ? (
                        // 收起展开的配置项
                        FI18n.i18nNext.tAuto('brr_resourcelisting_item_btn_showlesssetting')
                      ) : (
                        // 更多设置（展开依赖插件配置）
                        FI18n.i18nNext.tAuto('edit_exhibit_moresetting_btn')
                      )}
                    </FComponentsLib.FTextBtn>
                  )}
                </div>
              </div>
            </div>

            {open && hasFields && (
              <FAttrsAndConfigs
                dateSource={attrsSource}
                disabledKeys={[]}
                disabledNames={[]}
                onChange={(newDateSource, changedFields) => {
                  if (!changedFields.includes('value')) {
                    return;
                  }
                  if (!exhibitPresentableId || isMockItem) {
                    return;
                  }
                  void (async () => {
                    const rewriteProperty = attrsToDependencyRewritePayload(newDateSource);
                    if (rewriteProperty.length === 0) {
                      return;
                    }
                    const { ret, errCode, msg } =
                      await FServiceAPI.Exhibit.updatePresentableDependencyRewriteProperty({
                        presentableId: exhibitPresentableId,
                        nid: item.dependencyNid,
                        rewriteProperty,
                      });
                    if (ret !== 0 || errCode !== 0) {
                      fMessage(msg, 'error');
                      return;
                    }
                    fMessage(
                      // 保存成功
                      FI18n.i18nNext.t('saved_successfully'),
                      'success',
                    );
                    onAfterDependencyAttrsSave?.();
                  })();
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default FThemeDependencyPlugins;
